/**
 * UpgradeNudge — Kontextsensitiver Upgrade-Hinweis (2.0)
 *
 * Zeigt einen nicht-invasiven Hinweis wenn ein Feature für den aktuellen Plan
 * nicht verfügbar ist. Kein Modal, kein Blocker — nur ein Infoband.
 *
 * ── CTA-Logik ────────────────────────────────────────────────────────────────
 * upgradeTarget === "settings"  (Nutzer hat expliziten Plan):
 *   Primär:    „Plan in den Einstellungen ändern"  → /settings#plan-management
 *   Sekundär:  „Alle Pakete vergleichen"           → /pricing?highlight=<planId>
 *
 * upgradeTarget === "pricing"  (kein expliziter Plan):
 *   Primär:    „Passenden Plan finden"             → /pricing?highlight=<planId>
 *   Sekundär:  Planfinder-Link                     → /planfinder
 *
 * Props (alle optional außer hint):
 *   hint           {string}          — Hinweissatz, z.B. aus useFeatureGate()
 *   featureLabel   {string}          — Kurzer Feature-Name für konkreten Copy
 *   requiredPlan   {object|null}     — Plan-Objekt aus pricing.js
 *   upgradeTarget  {"settings"|"pricing"} — bevorzugter Upgrade-Touchpoint
 *   settingsHref   {string}          — Fertige Settings-URL (default: /settings#plan-management)
 *   pricingHref    {string}          — Fertige Pricing-URL (default: /pricing)
 *   className      {string}          — Zusätzliche Tailwind-Klassen
 *
 * Alle Felder außer hint können direkt aus useFeatureGate() destrukturiert werden:
 *   const gate = useFeatureGate("evergreen");
 *   {!gate.allowed && <UpgradeNudge {...gate} featureLabel="Evergreen-Planung" />}
 */

import { NavLink } from "react-router-dom";
import { ArrowRight, Settings, Sparkles } from "lucide-react";

const UpgradeNudge = ({
  hint,
  featureLabel,
  requiredPlan,
  upgradeTarget = "pricing",
  settingsHref = "/settings#plan-management",
  pricingHref,
  className = "",
}) => {
  if (!hint) return null;

  // Fallback für pricingHref wenn nicht als Prop übergeben
  const resolvedPricingHref = pricingHref ?? (requiredPlan
    ? `/pricing?highlight=${requiredPlan.id}`
    : "/pricing");

  // ── Copy je upgradeTarget ─────────────────────────────────────────────────
  const isSettingsTarget = upgradeTarget === "settings";

  // Konkrete Feature-Erwähnung im Hinweistext wenn featureLabel vorhanden
  const bodyText = featureLabel
    ? `${featureLabel} ist ab ${requiredPlan?.name ?? "einem höheren Plan"} verfügbar. Du kannst deinen Plan direkt in den Einstellungen anpassen.`
    : hint;

  const primaryLabel = isSettingsTarget
    ? "Plan in den Einstellungen ändern"
    : `${requiredPlan?.name ?? "Passenden Plan"} ansehen`;

  const secondaryLabel = isSettingsTarget
    ? "Alle Pakete vergleichen"
    : "Planfinder starten";

  const primaryHref = isSettingsTarget ? settingsHref : resolvedPricingHref;
  const secondaryHref = isSettingsTarget ? resolvedPricingHref : "/planfinder";

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3.5 bg-orange-50 border border-orange-200 rounded-xl ${className}`}
      role="note"
      aria-label={hint}
    >
      <Sparkles
        size={16}
        className="flex-shrink-0 mt-0.5 text-fox-fire"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        {/* Hinweistext */}
        <p className="text-sm text-orange-800 leading-snug">
          {bodyText}
        </p>

        {/* CTAs */}
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {/* Primärer CTA */}
          <NavLink
            to={primaryHref}
            className="inline-flex items-center gap-1.5 text-xs text-white bg-fox-fire hover:bg-orange-600 font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            {isSettingsTarget && <Settings size={11} />}
            {primaryLabel}
          </NavLink>

          {/* Sekundärer CTA */}
          <NavLink
            to={secondaryHref}
            className="inline-flex items-center gap-1 text-xs text-fox-fire hover:text-orange-700 font-medium transition-colors"
          >
            {secondaryLabel}
            <ArrowRight size={11} />
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default UpgradeNudge;
